import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { dirFor, type Locale } from './locales';

/**
 * Active-locale context. Persistence mirrors AuthProvider: read localStorage on init, write on
 * change. Default is 'fa', so with no stored preference the app is byte-for-byte identical to
 * today (Persian, RTL). On every locale change we also drive <html lang> and <html dir> so the
 * document direction follows the locale (base.css no longer hardcodes rtl on <body>).
 */

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);
const STORAGE_KEY = 'ct.locale';

function loadLocale(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'en' || raw === 'fa' ? raw : 'fa';
  } catch {
    return 'fa';
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => loadLocale());

  // Drive the document's lang/dir off the active locale, and persist the choice. Runs on mount
  // too, reconciling <html> with a restored preference. index.html ships dir="rtl" as the
  // pre-hydration default, so first paint is RTL before this effect runs.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = dirFor(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore storage errors
    }
  }, [locale]);

  const setLocale = useCallback((l: Locale) => setLocaleState(l), []);

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}
